import { Capacitor } from '@capacitor/core';
import { Clipboard } from '@capacitor/clipboard';
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
  type CSSProperties,
  useSyncExternalStore,
} from 'react';

import { useAppVersion } from 'src/hooks/use-app-version';

import { getCurrentAppVersion } from 'src/utils/app-version';
import { capacitorUpdaterService } from 'src/utils/Services/capacitor-updater.service';
import {
  type ApiCallRecord,
  subscribeApiCallHistory,
  getApiCallHistorySnapshot,
} from 'src/utils/http/api-call-history';

import { CONFIG } from 'src/environments/environment';
import { useSystemStore } from 'src/store/SystemStore';
import useAuthStore from 'src/auth/store/firebase/auth-store';
import { useConnectivityStore } from 'src/features/connectivity/connectivity-store';

import type { BootStatus, BootGateRecord } from './boot-types';
import { useBootStore, selectBootTimings, selectGateRecords } from './boot-store';
import {
  BOOT_DIAGNOSTICS_TAP_COUNT,
  BOOT_DIAGNOSTICS_TAP_WINDOW_MS,
  BOOT_DIAGNOSTICS_TRIGGER_SELECTOR,
} from './boot-diagnostics-trigger';

export {
  BOOT_DIAGNOSTICS_TAP_COUNT,
  BOOT_DIAGNOSTICS_TAP_WINDOW_MS,
  BOOT_DIAGNOSTICS_TRIGGER_SELECTOR,
  BOOT_DIAGNOSTICS_TRIGGER_ATTRIBUTE,
} from './boot-diagnostics-trigger';

/**
 * Dev-tap diagnostics for terminal boot/failure surfaces: 7 taps on the
 * version text within 3s open a read-only popup with the per-gate timeline,
 * connectivity/auth snapshots, and the (already-redacted) API-call history.
 *
 * The popup must work with NO router mounted and above ThemeProvider, so it
 * is MUI-free by construction: a fixed overlay with inline styles. It reads
 * global stores live, so it is equally useful mid-boot and mid-session.
 *
 * Redaction: API records are sanitized at capture time; this module
 * additionally never reads `requestBody` — rendering and the copied report
 * both go through an explicit field allowlist. No tokens or headers exist in
 * any consumed store.
 */

// ----------------------------------------------------------------------
// Tap trigger

type UseDevTapTriggerOptions = {
  targetSelector: string;
  onTrigger: () => void;
  requiredTapCount?: number;
  tapWindowMs?: number;
  disabled?: boolean;
};

/**
 * Same gesture feel as SystemPageDebugAccess: document-level click listener,
 * closest(selector) matching, same-element requirement, a FIXED window armed
 * at the first tap, reset on timeout/element-change/fire/unmount.
 */
export function useDevTapTrigger({
  targetSelector,
  onTrigger,
  requiredTapCount = BOOT_DIAGNOSTICS_TAP_COUNT,
  tapWindowMs = BOOT_DIAGNOSTICS_TAP_WINDOW_MS,
  disabled = false,
}: UseDevTapTriggerOptions): void {
  const tapCountRef = useRef(0);
  const tappedTargetRef = useRef<Element | null>(null);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTapSequence = useCallback(() => {
    tapCountRef.current = 0;
    tappedTargetRef.current = null;
    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
      tapTimerRef.current = null;
    }
  }, []);

  const handleTargetTap = useCallback(
    (event: MouseEvent) => {
      const { target } = event;
      if (!(target instanceof Element)) return;

      const tappedTarget = target.closest(targetSelector);
      if (!tappedTarget) return;

      if (tappedTargetRef.current !== tappedTarget) {
        resetTapSequence();
        tappedTargetRef.current = tappedTarget;
      }

      tapCountRef.current += 1;

      if (tapCountRef.current >= requiredTapCount) {
        resetTapSequence();
        onTrigger();
        return;
      }

      if (!tapTimerRef.current) {
        tapTimerRef.current = setTimeout(resetTapSequence, tapWindowMs);
      }
    },
    [onTrigger, requiredTapCount, resetTapSequence, tapWindowMs, targetSelector]
  );

  useEffect(() => {
    if (disabled) return undefined;
    document.addEventListener('click', handleTargetTap);
    return () => {
      document.removeEventListener('click', handleTargetTap);
      resetTapSequence();
    };
  }, [disabled, handleTargetTap, resetTapSequence]);
}

// ----------------------------------------------------------------------
// Singleton host

// First mounted host claims the document-level gesture; later hosts stay
// passive so BootGateHost's host and a page-embedded one (e.g. inside
// ConnectionRecoveryPage) never open two popups. A replacement tree's host
// claims after the previous one unmounts.
let activeDiagnosticsHostId: number | null = null;
let nextDiagnosticsHostId = 0;

export function BootDiagnosticsHost(): ReactNode {
  const [isActive, setIsActive] = useState(false);
  const [open, setOpen] = useState(false);
  const hostIdRef = useRef<number | null>(null);

  useEffect(() => {
    nextDiagnosticsHostId += 1;
    const hostId = nextDiagnosticsHostId;
    hostIdRef.current = hostId;
    if (activeDiagnosticsHostId === null) {
      activeDiagnosticsHostId = hostId;
      setIsActive(true);
    }
    return () => {
      if (activeDiagnosticsHostId === hostId) {
        activeDiagnosticsHostId = null;
      }
    };
  }, []);

  useDevTapTrigger({
    targetSelector: BOOT_DIAGNOSTICS_TRIGGER_SELECTOR,
    disabled: !isActive,
    onTrigger: () => setOpen(true),
  });

  if (!open) return null;
  return <BootDiagnosticsPopup onClose={() => setOpen(false)} />;
}

// ----------------------------------------------------------------------
// Data collection (allowlisted)

type DiagnosticApiCall = Pick<
  ApiCallRecord,
  | 'id'
  | 'method'
  | 'url'
  | 'startedAt'
  | 'completedAt'
  | 'durationMs'
  | 'status'
  | 'httpStatus'
  | 'errorCode'
  | 'errorMessage'
>;

// Explicit allowlist: requestBody must never enter the render or report path.
function toDiagnosticApiCall(record: ApiCallRecord): DiagnosticApiCall {
  const { id, method, url, startedAt, completedAt, durationMs, status, httpStatus } = record;
  const { errorCode, errorMessage } = record;
  return { id, method, url, startedAt, completedAt, durationMs, status, httpStatus, errorCode, errorMessage };
}

type PreMountBootError = { type?: string; message?: string; source?: string; ts?: number };

function readPreMountErrors(): PreMountBootError[] {
  const bootBag = (window as { __pcBoot?: { errors?: PreMountBootError[] } }).__pcBoot;
  return Array.isArray(bootBag?.errors) ? bootBag.errors : [];
}

export type BootDiagnosticsReportInput = {
  generatedAt: string;
  app: { version: string; otaBundle: string; environment: string; platform: string; native: boolean };
  bootStatus: BootStatus;
  timings: ReturnType<typeof selectBootTimings>;
  gates: BootGateRecord[];
  connectivity: {
    runtimeState: string;
    issue: string;
    message: string | null;
    deviceConnected: boolean;
    internetReachable: boolean | null;
    apiReachable: boolean | null;
  };
  auth: {
    authRestorationStatus: string;
    authInitialized: boolean;
    loading: boolean;
    authenticated: boolean;
    offlineIdentityVerified: boolean;
  };
  apiCaptureEnabled: boolean;
  apiCalls: DiagnosticApiCall[];
  preMountErrors: PreMountBootError[];
};

function collectReportInput(otaBundle: string): BootDiagnosticsReportInput {
  const bootState = useBootStore.getState();
  const connectivity = useConnectivityStore.getState();
  const auth = useAuthStore.getState();
  return {
    generatedAt: new Date().toISOString(),
    app: {
      version: getCurrentAppVersion(),
      otaBundle,
      environment: CONFIG.environment,
      platform: Capacitor.getPlatform(),
      native: Capacitor.isNativePlatform(),
    },
    bootStatus: bootState.bootStatus,
    timings: selectBootTimings(bootState),
    gates: selectGateRecords(bootState),
    connectivity: {
      runtimeState: connectivity.runtimeState,
      issue: connectivity.issue,
      message: connectivity.message,
      deviceConnected: connectivity.deviceConnected,
      internetReachable: connectivity.internetReachable,
      apiReachable: connectivity.apiReachable,
    },
    auth: {
      authRestorationStatus: auth.authRestorationStatus,
      authInitialized: auth.authInitialized,
      loading: auth.loading,
      authenticated: auth.authenticated,
      offlineIdentityVerified: auth.offlineIdentityVerified,
    },
    apiCaptureEnabled: useSystemStore.getState().apiCallCaptureEnabled,
    apiCalls: getApiCallHistorySnapshot().slice(0, 50).map(toDiagnosticApiCall),
    preMountErrors: readPreMountErrors(),
  };
}

function formatEpoch(at: number | null): string {
  return at === null ? '-' : new Date(at).toISOString();
}

function gateReportLines(gate: BootGateRecord): string[] {
  const lines = [
    `${gate.id} | ${gate.status}${gate.resuming ? ' (resuming)' : ''} | ${
      gate.durationMs === null ? '-' : `${gate.durationMs}ms`
    } | attempt ${gate.attempt}`,
  ];
  if (gate.error) {
    lines.push(`  error: ${gate.error.message} (${gate.error.classification})`);
  }
  if (gate.halt) {
    lines.push(`  halt: ${JSON.stringify(gate.halt)}`);
  }
  gate.lateSettlements.forEach((late) => {
    lines.push(`  late: ${late.outcome} at ${formatEpoch(late.at)} (${late.summary})`);
  });
  return lines;
}

/** Plain-text support report. Pure and synchronous for testability. */
export function buildBootDiagnosticsReport(input: BootDiagnosticsReportInput): string {
  const lines: string[] = [
    'PointCraft boot diagnostics',
    `generated: ${input.generatedAt}`,
    '',
    '[app]',
    `version: ${input.app.version}`,
    `ota bundle: ${input.app.otaBundle}`,
    `environment: ${input.app.environment}`,
    `platform: ${input.app.platform}${input.app.native ? ' (native)' : ''}`,
    '',
    '[boot]',
    `status: ${input.bootStatus}`,
    `started: ${formatEpoch(input.timings.startedAt)}`,
    `gates settled: ${formatEpoch(input.timings.gatesSettledAt)}`,
    `router stage: ${formatEpoch(input.timings.routerStageAt)}`,
    `completed: ${formatEpoch(input.timings.completedAt)}`,
    `web fast path: ${input.timings.webFastPath ? 'yes' : 'no'}`,
    '',
    '[boot gates]',
    ...(input.gates.length === 0
      ? ['(controller not started)']
      : input.gates.flatMap(gateReportLines)),
    '',
    '[connectivity]',
    `runtimeState: ${input.connectivity.runtimeState}`,
    `issue: ${input.connectivity.issue}`,
    `message: ${input.connectivity.message ?? '-'}`,
    `deviceConnected: ${input.connectivity.deviceConnected}`,
    `internetReachable: ${String(input.connectivity.internetReachable)}`,
    `apiReachable: ${String(input.connectivity.apiReachable)}`,
    '',
    '[auth]',
    `authRestorationStatus: ${input.auth.authRestorationStatus}`,
    `authInitialized: ${input.auth.authInitialized}`,
    `loading: ${input.auth.loading}`,
    `authenticated: ${input.auth.authenticated}`,
    `offlineIdentityVerified: ${input.auth.offlineIdentityVerified}`,
    '',
    `[api calls] (capture: ${input.apiCaptureEnabled ? 'on' : 'off'})`,
    ...(input.apiCalls.length === 0
      ? ['(none recorded)']
      : input.apiCalls.map(
          (call) =>
            `${call.startedAt} ${call.method} ${call.url} -> ${call.status}${
              call.httpStatus === null ? '' : ` ${call.httpStatus}`
            }${call.durationMs === null ? '' : ` ${call.durationMs}ms`}${
              call.errorCode ? ` ${call.errorCode}` : ''
            }${call.errorMessage ? ` ${call.errorMessage}` : ''}`
        )),
  ];
  if (input.preMountErrors.length > 0) {
    lines.push('', '[pre-mount errors]');
    input.preMountErrors.forEach((error) => {
      lines.push(`- ${error.type ?? 'error'} ${error.message ?? ''} ${error.source ?? ''}`.trim());
    });
  }
  return lines.join('\n');
}

// ----------------------------------------------------------------------
// Popup

const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const styles: Record<string, CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background: 'rgba(22, 28, 36, 0.72)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  panel: {
    width: '100%',
    maxWidth: 680,
    maxHeight: '85vh',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    background: '#1C242F',
    border: '1px solid rgba(145, 158, 171, 0.16)',
    borderRadius: 16,
    padding: '20px 20px calc(20px + env(safe-area-inset-bottom))',
    color: '#B6C2D0',
    fontFamily: "'Public Sans', system-ui, -apple-system, sans-serif",
    fontSize: 13,
    lineHeight: 1.55,
    textAlign: 'left',
    outline: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 4,
  },
  title: { margin: 0, fontSize: 16, fontWeight: 800, color: '#FFFFFF' },
  timestamp: { fontFamily: MONO_FONT, fontSize: 11, color: '#7E8B99' },
  closeButton: {
    flex: 'none',
    width: 44,
    height: 44,
    border: 0,
    borderRadius: 10,
    background: 'rgba(145, 158, 171, 0.12)',
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 700,
    cursor: 'pointer',
  },
  sectionTitle: {
    margin: '18px 0 6px',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    color: '#7E8B99',
  },
  row: { display: 'flex', gap: 10, padding: '2px 0', alignItems: 'baseline' },
  rowLabel: { flex: 'none', width: 168, color: '#7E8B99' },
  rowValue: { fontFamily: MONO_FONT, color: '#E4EAF2', overflowWrap: 'anywhere' },
  gateBlock: {
    padding: '6px 0',
    borderBottom: '1px solid rgba(145, 158, 171, 0.1)',
    fontFamily: MONO_FONT,
  },
  apiRow: {
    padding: '5px 0',
    borderBottom: '1px solid rgba(145, 158, 171, 0.08)',
    fontFamily: MONO_FONT,
    fontSize: 12,
    overflowWrap: 'anywhere',
  },
  hint: { color: '#7E8B99', fontStyle: 'italic' },
  copyButton: {
    marginTop: 18,
    minHeight: 48,
    width: '100%',
    border: 0,
    borderRadius: 10,
    background: '#5BE49B',
    color: '#10211A',
    fontSize: 15,
    fontWeight: 800,
    cursor: 'pointer',
  },
};

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={styles.row}>
      <span style={styles.rowLabel}>{label}</span>
      <span style={styles.rowValue}>{value}</span>
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <h3 style={styles.sectionTitle}>{children}</h3>;
}

const API_CALLS_RENDER_LIMIT = 20;

export function BootDiagnosticsPopup({ onClose }: { onClose: () => void }) {
  const appVersion = useAppVersion();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const openedAtRef = useRef(new Date().toISOString());
  const [otaBundle, setOtaBundle] = useState('—');
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  const bootStatus = useBootStore((state) => state.bootStatus);
  const gates = useBootStore(selectGateRecords);
  const timings = useBootStore(selectBootTimings);

  const runtimeState = useConnectivityStore((state) => state.runtimeState);
  const issue = useConnectivityStore((state) => state.issue);
  const message = useConnectivityStore((state) => state.message);
  const deviceConnected = useConnectivityStore((state) => state.deviceConnected);
  const internetReachable = useConnectivityStore((state) => state.internetReachable);
  const apiReachable = useConnectivityStore((state) => state.apiReachable);

  const authRestorationStatus = useAuthStore((state) => state.authRestorationStatus);
  const authInitialized = useAuthStore((state) => state.authInitialized);
  const authLoading = useAuthStore((state) => state.loading);
  const authenticated = useAuthStore((state) => state.authenticated);
  const offlineIdentityVerified = useAuthStore((state) => state.offlineIdentityVerified);

  const apiCaptureEnabled = useSystemStore((state) => state.apiCallCaptureEnabled);
  const apiCalls = useSyncExternalStore(subscribeApiCallHistory, getApiCallHistorySnapshot);
  const preMountErrors = readPreMountErrors();

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    let mounted = true;
    capacitorUpdaterService
      .getCurrentVersion()
      .then((result) => {
        if (!mounted) return;
        setOtaBundle(`${result.bundle.version} (${result.bundle.id})`);
      })
      .catch(() => {
        if (mounted) setOtaBundle('unavailable');
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleCopy = async () => {
    const report = buildBootDiagnosticsReport(collectReportInput(otaBundle));
    try {
      await Clipboard.write({ string: report });
      setCopyState('copied');
    } catch {
      try {
        await navigator.clipboard?.writeText(report);
        setCopyState('copied');
      } catch {
        setCopyState('failed');
      }
    }
    setTimeout(() => setCopyState('idle'), 2_000);
  };

  const renderedApiCalls = apiCalls.slice(0, API_CALLS_RENDER_LIMIT).map(toDiagnosticApiCall);
  const olderApiCallCount = Math.max(0, apiCalls.length - API_CALLS_RENDER_LIMIT);

  let copyLabel = 'Copy details';
  if (copyState === 'copied') copyLabel = 'Copied ✓';
  if (copyState === 'failed') copyLabel = 'Copy failed';

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div style={styles.backdrop} onClick={onClose}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Boot diagnostics"
        tabIndex={-1}
        style={styles.panel}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Boot diagnostics</h2>
            <div style={styles.timestamp}>opened {openedAtRef.current}</div>
          </div>
          <button type="button" style={styles.closeButton} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <SectionTitle>App</SectionTitle>
        <Row label="Version" value={`v${appVersion}`} />
        <Row label="OTA bundle" value={otaBundle} />
        <Row label="Environment" value={CONFIG.environment} />
        <Row
          label="Platform"
          value={`${Capacitor.getPlatform()}${Capacitor.isNativePlatform() ? ' (native)' : ''}`}
        />

        <SectionTitle>Boot gates</SectionTitle>
        <Row label="Boot status" value={bootStatus} />
        <Row label="Started" value={formatEpoch(timings.startedAt)} />
        <Row label="Router stage" value={formatEpoch(timings.routerStageAt)} />
        {gates.length === 0 && <div style={styles.hint}>Controller not started.</div>}
        {gates.map((gate) => (
          <div key={gate.id} style={styles.gateBlock}>
            <div>
              {gate.id} · {gate.status}
              {gate.resuming ? ' (resuming)' : ''} ·{' '}
              {gate.durationMs === null ? '—' : `${gate.durationMs}ms`} · attempt {gate.attempt}
            </div>
            {gate.error && (
              <div style={{ color: '#FFAB91' }}>
                error: {gate.error.message} ({gate.error.classification})
              </div>
            )}
            {gate.halt && <div>halt: {JSON.stringify(gate.halt)}</div>}
            {gate.lateSettlements.map((late) => (
              <div key={late.at} style={{ color: '#7E8B99' }}>
                late {late.outcome} at {formatEpoch(late.at)}: {late.summary}
              </div>
            ))}
          </div>
        ))}

        <SectionTitle>Connectivity</SectionTitle>
        <Row label="Runtime state" value={runtimeState} />
        <Row label="Issue" value={issue} />
        <Row label="Message" value={message ?? '—'} />
        <Row label="Device connected" value={String(deviceConnected)} />
        <Row label="Internet reachable" value={String(internetReachable)} />
        <Row label="API reachable" value={String(apiReachable)} />

        <SectionTitle>Auth</SectionTitle>
        <Row label="Restoration status" value={authRestorationStatus} />
        <Row label="Initialized" value={String(authInitialized)} />
        <Row label="Loading" value={String(authLoading)} />
        <Row label="Authenticated" value={String(authenticated)} />
        <Row label="Offline identity" value={String(offlineIdentityVerified)} />

        <SectionTitle>API calls</SectionTitle>
        {!apiCaptureEnabled && (
          <div style={styles.hint}>
            API call capture is off — enable it in System → Debug to record requests.
          </div>
        )}
        {renderedApiCalls.length === 0 && <div style={styles.hint}>No requests recorded.</div>}
        {renderedApiCalls.map((call) => (
          <div key={call.id} style={styles.apiRow}>
            {call.startedAt} {call.method} {call.url} → {call.status}
            {call.httpStatus === null ? '' : ` ${call.httpStatus}`}
            {call.durationMs === null ? '' : ` · ${call.durationMs}ms`}
            {call.errorCode ? ` · ${call.errorCode}` : ''}
            {call.errorMessage ? ` · ${call.errorMessage}` : ''}
          </div>
        ))}
        {olderApiCallCount > 0 && (
          <div style={styles.hint}>+{olderApiCallCount} older included in the copied report.</div>
        )}

        {preMountErrors.length > 0 && (
          <>
            <SectionTitle>Pre-mount errors</SectionTitle>
            {preMountErrors.map((error, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} style={styles.apiRow}>
                {error.type ?? 'error'} {error.message ?? ''} {error.source ?? ''}
              </div>
            ))}
          </>
        )}

        <button type="button" style={styles.copyButton} onClick={handleCopy}>
          {copyLabel}
        </button>
      </div>
    </div>
  );
}
